import React, { useEffect, useState } from 'react';
import { jobsAPI } from '../../lib/api';
import { Card } from '../ui/card';

const LEVELS = {
  strong: { label: 'Strong', color: '#059669', bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.3)' },
  adequate: { label: 'Adequate', color: '#D97706', bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.3)' },
  thin: { label: 'Too thin', color: '#DC2626', bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.3)' },
};

// How reliably candidates can be scored against this JD, and exactly what to
// add. Deterministic backend check — no AI credits, safe to call while typing.
export function JDQualityCard({ job, compact = false }) {
  const [grade, setGrade] = useState(null);

  const title = job?.title || '';
  const description = job?.description || '';
  const required = job?.required_skills;
  const nice = job?.nice_to_have_skills;

  useEffect(() => {
    if (!description.trim()) { setGrade(null); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      jobsAPI.jdQuality({
        title,
        description,
        required_skills: Array.isArray(required)
          ? required
          : String(required || '').split(',').map((s) => s.trim()).filter(Boolean),
        nice_to_have_skills: Array.isArray(nice)
          ? nice
          : String(nice || '').split(',').map((s) => s.trim()).filter(Boolean),
      })
        .then((res) => { if (!cancelled) setGrade(res.data); })
        .catch(() => { if (!cancelled) setGrade(null); });
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [title, description, required, nice]);

  if (!grade) return null;
  const meta = LEVELS[grade.level] || LEVELS.adequate;

  const body = (
    <div data-testid="jd-quality-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: compact ? '0.8125rem' : '0.9375rem', fontWeight: 600, margin: 0 }}>
          JD Scoreability
        </h3>
        <span
          data-testid="jd-quality-level"
          style={{
            padding: '0.1875rem 0.5rem', borderRadius: 999, fontSize: '0.6875rem', fontWeight: 600,
            background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
          }}
        >
          {meta.label} &middot; <span data-testid="jd-quality-score">{grade.score}</span>/100
        </span>
      </div>

      <div style={{ height: 6, borderRadius: 999, background: 'hsl(var(--muted))', overflow: 'hidden', marginBottom: '0.625rem' }}>
        <div style={{ height: '100%', width: `${grade.score}%`, background: meta.color, transition: 'width 0.4s ease' }} />
      </div>

      <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.5, margin: 0 }}>
        {grade.note}
      </p>

      {grade.missing?.length > 0 && (
        <div data-testid="jd-quality-missing" style={{ marginTop: '0.75rem' }}>
          <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem' }}>
            Add to tighten scores
          </div>
          <ul style={{ margin: 0, paddingLeft: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {grade.missing.slice(0, compact ? 3 : 6).map((m) => (
              <li key={m.key} style={{ fontSize: '0.75rem', color: '#002A38', lineHeight: 1.45 }}>{m.fix}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  if (compact) return body;
  return (
    <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.25rem' }}>
      {body}
    </Card>
  );
}
